/**
 * @file useCameraScanner.ts
 * @description Камера + декодер для диалога скана (phase19.22 шаг 5):
 *   открыть заднюю камеру, показать её в `<video>`, читать кадры до первого
 *   кода, фонарик, понятная ошибка. Стрим закрывается ВСЕГДА — при
 *   распознавании, закрытии диалога и размонтировании (иначе на телефоне
 *   горит индикатор камеры и садится батарея).
 *
 * «Камеры нет» (`noCamera`) хук только сообщает: кнопку прячет вызывающий
 * при закрытии диалога — иначе диалог исчез бы вместе с текстом ошибки.
 *
 * `<video>` передаётся колбэк-рефом (`setVideo`): попап диалога монтируется
 * порталом не в первый рендер, и обычный `useRef` в эффекте был бы `null`.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import {
  detectDecoderKind,
  startNativeDecoder,
  startZxingDecoder,
  type DecoderHandle,
} from "./camera-decoders";
import { cameraErrorText, normalizeScannedCode } from "./camera-scan";

/** Фаза диалога: камера включается → идёт поиск кода → ошибка. */
export type CameraScanStatus = "starting" | "scanning" | "error";

interface Params {
  open: boolean;
  /** Первый распознанный код (уже нормализованный). Стрим к этому моменту остановлен. */
  onDetected: (code: string) => void;
}

export function useCameraScanner({ open, onDetected }: Params) {
  const [video, setVideo] = useState<HTMLVideoElement | null>(null);
  const [status, setStatus] = useState<CameraScanStatus>("starting");
  const [error, setError] = useState<string | null>(null);
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [noCamera, setNoCamera] = useState(false);
  const trackRef = useRef<MediaStreamTrack | null>(null);
  const onDetectedRef = useRef(onDetected);

  useEffect(() => {
    onDetectedRef.current = onDetected;
  }, [onDetected]);

  useEffect(() => {
    if (!open || !video) return;
    let cancelled = false;
    let done = false;
    let stream: MediaStream | null = null;
    let decoder: DecoderHandle | null = null;
    setStatus("starting");
    setError(null);
    setTorchAvailable(false);
    setTorchOn(false);
    setNoCamera(false);

    const stopAll = () => {
      decoder?.stop();
      decoder = null;
      stream?.getTracks().forEach((t) => t.stop());
      stream = null;
      trackRef.current = null;
      video.srcObject = null;
    };

    const handle = (raw: string) => {
      const code = normalizeScannedCode(raw);
      if (done || cancelled || !code) return;
      done = true;
      stopAll();
      onDetectedRef.current(code);
    };

    void (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        if (cancelled) return stopAll();
        video.srcObject = stream;
        video.muted = true;
        await video.play();
        const track = stream.getVideoTracks()[0] ?? null;
        trackRef.current = track;
        setTorchAvailable(track?.getCapabilities?.().torch === true);

        const { kind, supported } = await detectDecoderKind();
        if (cancelled) return stopAll();
        decoder =
          kind === "native"
            ? startNativeDecoder(video, supported, handle)
            : await startZxingDecoder(video, handle);
        // Закрыли, пока грузился zxing, или он распознал первый же кадр
        // синхронно (стрим уже погашен в `handle`) — декодер есть, гасим и его.
        if (cancelled || done) return stopAll();
        setStatus("scanning");
      } catch (err) {
        stopAll();
        if (cancelled) return;
        const info = cameraErrorText(err);
        setNoCamera(info.noCamera);
        setError(info.text);
        setStatus("error");
      }
    })();

    return () => {
      cancelled = true;
      stopAll();
    };
  }, [open, video]);

  /** Фонарик: нестандартное `torch` (Chrome Android); сбой — просто не включился. */
  const toggleTorch = useCallback(async () => {
    const track = trackRef.current;
    if (!track) return;
    const next = !torchOn;
    try {
      await track.applyConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch {
      setTorchAvailable(false);
    }
  }, [torchOn]);

  return { setVideo, status, error, noCamera, torchAvailable, torchOn, toggleTorch };
}
